import { createReducer, on } from "@ngrx/store";
import { INotification } from "src/app/core/models/notification";
import { SchoolNotificationSettings, StudentNotificationSettings } from "../../models/messaging";
import { resetAppState } from "../global/global.actions";
import { NotificationActions } from "./notification.actions";

export interface NotificationState {
  notifications: INotification[];
  studentNotificationSettings: Record<string, StudentNotificationSettings>;
  schoolNotificationSettings: Record<string, SchoolNotificationSettings>;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  loading: false,
  error: null,
  studentNotificationSettings: {},
  schoolNotificationSettings: {}
};

export const notificationReducer = createReducer(
  initialState,

  // Handle Add Notification
  on(NotificationActions.addNotification, (state, { notification }) => ({
    ...state,
    notifications: [notification, ...state.notifications],
  })),

  // Handle Mark As Read
  on(NotificationActions.markAsRead, (state, { id }) => ({
    ...state,
    notifications: state.notifications.map(notification =>
      notification.id === id ? { ...notification, isRead: true } : notification
    ),
  })),

  // Handle Delete Notification
  on(NotificationActions.deleteNotification, (state, { id }) => ({
    ...state,
    notifications: state.notifications.filter(notification => notification.id !== id),
  })),

  // Handle Load Notifications (start loading)
  on(NotificationActions.loadNotifications, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  // Handle Load Notifications Success
  on(NotificationActions.loadNotificationsSuccess, (state, { notifications }) => ({
    ...state,
    notifications,
    loading: false,
    error: null,
  })),

  // Handle Load Notifications Failure
  on(NotificationActions.loadNotificationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Handling the 'Dismiss All' action
  on(NotificationActions.dismissAll, (state) => ({
    ...state,
    notifications: [], // Clear all notifications
    loading: false,
    error: null
  })),

  // Handle Mark All As Read
  on(NotificationActions.readAll, (state) => ({
    ...state,
    notifications: state.notifications.map(notification => ({
      ...notification,
      read: true  // Mark each notification as read
    }))
  })),

  // Handling initializing student settings
  on(NotificationActions.initialiseStudentSettings, (state, { students }) => {
    const newSettings = students.reduce<Record<string, StudentNotificationSettings>>((acc, student) => {
      acc[student.id] = {
        push: true,           // Initialize MyHub preference
        email: true,          // Initialize Email preference
        sms: true,            // Initialize SMS preference
        reportCards: true,    // Initialize Report Cards available preference
        homework: true,       // Initialize Homework added preference
        marks: true,          // Initialize Marks available preference
        timetables: true,     // Initialize Timetable available preference
        matchResults: true,   // Initialize Match results preference
        attendance: true,     // Initialize Attendance preference
        travelAlert: true
      } as StudentNotificationSettings;
      return acc;
    }, {});

    return {
      ...state,
      studentNotificationSettings: {
        ...state.studentNotificationSettings,
        ...newSettings
      }
    };
  }),

  // Handling updating a specific student's settings
  on(NotificationActions.updateStudentSettings, (state, { studentId, settings }) => ({
    ...state,
    studentNotificationSettings: {
      ...state.studentNotificationSettings,
      [studentId]: {
        ...state.studentNotificationSettings[studentId],
        ...settings
      }
    }
  })),

  // Handling initializing school settings
  on(NotificationActions.initialiseSchoolSettings, (state, { schools }) => {
    const newSettings = schools.reduce<Record<string, SchoolNotificationSettings>>((acc, school) => {
      acc[school.id] = {
        push: true,              // Initialize Push notifications preference
        email: true,             // Initialize Email notifications preference
        sms: true,               // Initialize SMS notifications preference
        announcements: true,     // Initialize Announcements notifications preference
        calendar: true,          // Initialize Calendar notifications preference
        directMessages: true,    // Initialize Direct Messages notifications preference
        groups: true             // Initialize Groups notifications preference
      } as SchoolNotificationSettings;
      return acc;
    }, {});

    return {
      ...state,
      schoolNotificationSettings: {
        ...state.schoolNotificationSettings,
        ...newSettings
      }
    };
  }),

  // Handling updating a specific school's settings
  on(NotificationActions.updateSchoolSettings, (state, { schoolId, settings }) => ({
    ...state,
    schoolNotificationSettings: {
      ...state.schoolNotificationSettings,
      [schoolId]: {
        ...state.schoolNotificationSettings[schoolId],
        ...settings
      }
    }
  })),
  on(resetAppState, () => initialState)
);

